import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';
import { ExternalTaskFilter } from '@/services/api/firmsService';

import { PopularTaskNames } from './PopularTaskNames';
import { TagInput } from './TagInput';

interface ExternalTaskFiltersProps {
  value: ExternalTaskFilter;
  onChange: (value: ExternalTaskFilter) => void;
  disabled?: boolean;
  firmId: string;
}

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
] as const;

interface FilterSectionProps {
  label: string;
  description: string;
  children: React.ReactNode;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  label,
  description,
  children,
}) => (
  <div className="space-y-2">
    <div>
      <div className="text-sm font-medium text-slate-900">{label}</div>
      <p className="text-xs text-slate-500">{description}</p>
    </div>
    {children}
  </div>
);

interface StatusOptionProps {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const StatusOption: React.FC<StatusOptionProps> = ({
  label,
  isSelected,
  onClick,
  disabled,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'group flex items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-all duration-150',
      disabled
        ? 'cursor-not-allowed opacity-50'
        : 'hover:bg-slate-50 active:bg-slate-100',
    )}
  >
    <div
      className={cn(
        'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded border-2 transition-all duration-150',
        isSelected
          ? 'border-emerald-500 bg-emerald-500'
          : 'border-slate-300 bg-white group-hover:border-slate-400',
        disabled && 'border-slate-200 bg-slate-50',
      )}
    >
      {isSelected && (
        <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} />
      )}
    </div>
    <span
      className={cn(
        'text-sm font-medium transition-colors',
        isSelected ? 'text-slate-900' : 'text-slate-600',
        disabled && 'text-slate-400',
      )}
    >
      {label}
    </span>
  </button>
);

export const ExternalTaskFilters: React.FC<ExternalTaskFiltersProps> = ({
  value,
  onChange,
  disabled = false,
  firmId,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(() => {
    // Auto-expand if advanced filters are already set
    return (
      (value.name_not_contains?.length ?? 0) > 0 ||
      (value.statuses?.length ?? 0) > 0
    );
  });

  const updateField = <K extends keyof ExternalTaskFilter>(
    field: K,
    fieldValue: ExternalTaskFilter[K],
  ) => {
    if (disabled) return;
    onChange({ ...value, [field]: fieldValue });
  };

  const toggleStatus = (statusValue: string) => {
    if (disabled) return;
    const current = value.statuses || [];
    if (
      current.includes(statusValue as (typeof STATUS_OPTIONS)[number]['value'])
    ) {
      updateField(
        'statuses',
        current.filter((s: string) => s !== statusValue) as typeof current,
      );
    } else {
      updateField('statuses', [...current, statusValue] as typeof current);
    }
  };

  const advancedFilterCount =
    (value.name_not_contains?.length ?? 0) + (value.statuses?.length ?? 0);

  const mainFilterCount =
    (value.name_contains?.length ?? 0) +
    (value.description_contains?.length ?? 0) +
    (value.assignee_names?.length ?? 0);

  return (
    <div
      className={cn('space-y-5', disabled && 'pointer-events-none opacity-60')}
    >
      {/* Main Filters - Always Visible */}
      <FilterSection
        label="Name Contains"
        description="Match tasks whose name contains any of these terms"
      >
        <TagInput
          values={value.name_contains || []}
          onChange={(tags) => updateField('name_contains', tags)}
          placeholder="Type keyword and press Enter..."
          disabled={disabled}
        />
      </FilterSection>

      {/* Popular Task Names Suggestions */}
      <PopularTaskNames
        firmId={firmId}
        selectedNames={value.name_contains || []}
        onAddName={(name) => {
          const current = value.name_contains || [];
          if (!current.includes(name)) {
            updateField('name_contains', [...current, name]);
          }
        }}
        onRemoveName={(name) => {
          const current = value.name_contains || [];
          updateField(
            'name_contains',
            current.filter((n: string) => n !== name),
          );
        }}
        disabled={disabled}
        externalFilter={value}
      />

      <FilterSection
        label="Description Contains"
        description="Match tasks whose description contains any of these terms"
      >
        <TagInput
          values={value.description_contains || []}
          onChange={(tags) => updateField('description_contains', tags)}
          placeholder="Type keyword and press Enter..."
          disabled={disabled}
        />
      </FilterSection>

      <FilterSection
        label="Assignee Name Contains"
        description="Match tasks assigned to users whose name contains these terms"
      >
        <TagInput
          values={value.assignee_names || []}
          onChange={(tags) => updateField('assignee_names', tags)}
          placeholder="Type assignee name and press Enter..."
          disabled={disabled}
        />
      </FilterSection>

      {/* Advanced Section - Collapsible */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          disabled={disabled}
          className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-slate-50"
        >
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-900">
              Advanced Filters
            </span>
            {advancedFilterCount > 0 && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                {advancedFilterCount} active
              </span>
            )}
          </div>
          <ChevronDownIcon
            className={cn(
              'h-5 w-5 text-slate-400 transition-transform duration-200',
              showAdvanced && 'rotate-180',
            )}
          />
        </button>

        <div
          className={cn(
            'grid transition-all duration-200 ease-out',
            showAdvanced ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="overflow-hidden">
            <div className="space-y-5 border-t border-slate-100 px-4 py-4">
              {/* Exclude by Name */}
              <FilterSection
                label="Exclude if Name Contains"
                description="Exclude tasks whose name contains any of these terms"
              >
                <TagInput
                  values={value.name_not_contains || []}
                  onChange={(tags) => updateField('name_not_contains', tags)}
                  placeholder="Type exclusion term and press Enter..."
                  disabled={disabled}
                />
              </FilterSection>

              {/* Status Filter */}
              <div className="space-y-2">
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    Status
                  </div>
                  <p className="text-xs text-slate-500">
                    Match tasks with any of these statuses (leave empty for all)
                  </p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {STATUS_OPTIONS.map((status) => (
                    <StatusOption
                      key={status.value}
                      label={status.label}
                      isSelected={
                        value.statuses?.includes(status.value) ?? false
                      }
                      onClick={() => toggleStatus(status.value)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Summary */}
      {mainFilterCount + advancedFilterCount > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
          <span className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">
              {mainFilterCount + advancedFilterCount}
            </span>{' '}
            filter{mainFilterCount + advancedFilterCount !== 1 && 's'}{' '}
            configured
          </span>
          <button
            type="button"
            onClick={() => {
              if (disabled) return;
              onChange({
                ...value,
                name_contains: [],
                description_contains: [],
                assignee_names: [],
                name_not_contains: [],
                statuses: [],
              });
            }}
            disabled={disabled}
            className="text-sm font-medium text-slate-500 transition-colors hover:text-slate-700"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};
